import { Button } from "~/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/ui/alert-dialog"
import Text from "~/ui/typography"
import { WalletDisplay } from "~/ui/wallet-display"
import trashIcon from "~/images/icons/trash"
import { UserPhoneStatus, UserProfileFragment } from "~/__generated__/graphql"

interface UserInfoSectionProps {
  user: UserProfileFragment
  isEditable: boolean
  onAddEmail?: () => void
  onAddPhone?: () => void
  onAddWallet?: () => void
  onDeleteEmail?: (id: string) => void
  onDeletePhone?: (id: string) => void
  onDeleteWallet?: (id: string) => void
  onVerifyEmail?: (id: string, email: string) => void
  onVerifyPhone?: (id: string, phone: string, phoneFormatted: string) => void
  deleteEmailLoading?: boolean
  deletePhoneLoading?: boolean
  deleteWalletLoading?: boolean
}

export const UserInfoSection = ({
  user,
  isEditable,
  onAddEmail,
  onAddPhone,
  onAddWallet,
  onDeleteEmail,
  onDeletePhone,
  onDeleteWallet,
  onVerifyEmail,
  onVerifyPhone,
  deleteEmailLoading = false,
  deletePhoneLoading = false,
  deleteWalletLoading = false,
}: UserInfoSectionProps) => {
  return (
    <div className="mb-8 space-y-6">
      <Text variant="heading4" as="h3">
        Account
      </Text>
      <hr />

      <div className="space-y-6">
        {/* Emails */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Text variant="subheader_bold">Email</Text>
            {isEditable && (
              <Button variant="default" size="sm" onClick={onAddEmail}>
                Add Email
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {user.emails && user.emails.length > 0 ? (
              user.emails.map((email) => (
                <div key={email.id} className="group grid grid-cols-[1fr_auto] items-center gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">{email.email}</span>
                      <span
                        className={`text-sm ${
                          email.status === "verified" ? "text-blue-600" : "text-orange-600"
                        }`}
                      >
                        {email.status === "verified" ? "verified" : "pending verification"}
                      </span>
                    </div>
                  </div>
                  {isEditable && (
                    <div className="flex items-center gap-2">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="rounded p-1 opacity-0 transition-opacity hover:bg-gray-100 group-hover:opacity-100">
                            <img
                              src={trashIcon.src}
                              alt="Delete email"
                              width={trashIcon.width}
                              height={trashIcon.height}
                              className="text-gray-400"
                            />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Email</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this email address? This action cannot
                              be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDeleteEmail?.(email.id)}
                              disabled={deleteEmailLoading}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {deleteEmailLoading ? "Deleting..." : "Delete"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      {email.status === "pending" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onVerifyEmail?.(email.id, email.email)}
                          className="w-24"
                        >
                          Verify
                        </Button>
                      ) : (
                        <div className="w-24" />
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-gray-600">No email addresses added</div>
            )}
          </div>
        </div>

        {/* Phones */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Text variant="subheader_bold">Phone</Text>
            {isEditable && (
              <Button variant="default" size="sm" onClick={onAddPhone}>
                Add Phone
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {user.phones && user.phones.length > 0 ? (
              user.phones.map((phone) => (
                <div key={phone.id} className="group grid grid-cols-[1fr_auto] items-center gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">{phone.phoneFormatted}</span>
                      <span
                        className={`text-sm ${
                          phone.status === UserPhoneStatus.Verified
                            ? "text-blue-600"
                            : "text-orange-600"
                        }`}
                      >
                        {phone.status === UserPhoneStatus.Verified
                          ? "verified"
                          : "pending verification"}
                      </span>
                    </div>
                  </div>
                  {isEditable && (
                    <div className="flex items-center gap-2">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="rounded p-1 opacity-0 transition-opacity hover:bg-gray-100 group-hover:opacity-100">
                            <img
                              src={trashIcon.src}
                              alt="Delete phone"
                              width={trashIcon.width}
                              height={trashIcon.height}
                              className="text-gray-400"
                            />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Phone</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this phone number? This action cannot
                              be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDeletePhone?.(phone.id)}
                              disabled={deletePhoneLoading}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {deletePhoneLoading ? "Deleting..." : "Delete"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      {phone.status === "PENDING" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            onVerifyPhone?.(phone.id, phone.phone || "", phone.phoneFormatted || "")
                          }
                          className="w-24"
                        >
                          Verify
                        </Button>
                      ) : (
                        <div className="w-24" />
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-gray-600">No phone numbers added</div>
            )}
          </div>
        </div>

        {/* Wallets */}
        <div className="grid grid-cols-[1fr_auto]">
          <div className="flex flex-col">
            <Text variant="subheader_bold">Wallet</Text>
            {user.wallets && user.wallets?.length > 0
              ? user.wallets?.map((wallet) => (
                  <div
                    key={wallet.id}
                    className="group grid grid-cols-[1fr_auto] items-center gap-2"
                  >
                    <div>
                      <WalletDisplay
                        publicKey={wallet.publicKey}
                        publicKeyFormatted={wallet.publicKeyFormatted}
                        showVerified
                      />
                    </div>
                    {isEditable && (
                      <>
                        {user.wallets && user.wallets?.length > 1 ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="rounded p-1 opacity-0 transition-opacity hover:bg-gray-100 group-hover:opacity-100">
                                <img
                                  src={trashIcon.src}
                                  alt="Delete wallet"
                                  width={trashIcon.width}
                                  height={trashIcon.height}
                                  className="text-gray-400"
                                />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Wallet</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this wallet? This action cannot be
                                  undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDeleteWallet?.(wallet.id)}
                                  disabled={deleteWalletLoading}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  {deleteWalletLoading ? "Deleting..." : "Delete"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <div className="rounded p-1 opacity-0 group-hover:opacity-50">
                            <img
                              src={trashIcon.src}
                              alt="Cannot delete last wallet"
                              width={trashIcon.width}
                              height={trashIcon.height}
                              className="text-gray-300"
                              title="Cannot delete your last wallet"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))
              : null}
          </div>
          <div className="grid grid-cols-[1fr_auto] items-center gap-4">
            <div>
              <div className="text-gray-600">
                {user.wallets && user.wallets.length > 0 ? "" : "No wallet addresses added"}
              </div>
            </div>
            {isEditable && (
              <Button variant="default" size="sm" className="w-24" onClick={onAddWallet}>
                Add Wallet
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
